import React from "react";
import dayjs from "dayjs";
import { Grid, Link, Paper, Typography, makeStyles } from "@material-ui/core";
import { Tweet } from "./Tweets";

const useStyles = makeStyles(() => ({
    profile: {
        marginRight: "1ex",
    },
    tweetContainer: {
        padding: "1ex",
    },
    tweetRightBox: {
        flexGrow: 2,
        maxWidth: "100%",
    },
    footer: {
        listStyle: "none",
        paddingLeft: 0,
        "& li": {
            "&:before": {
                content: '"\\2005/\\2005"',
            },
            "&:first-child:before": {
                content: '""',
            },
        },
    },
    source: {
        "& a": {
            color: "inherit",
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        },
    },
}));

type Props = {
    tweet: Tweet;
};

export function TweetCell(props: Props) {
    const tweet = props.tweet.retweeted_status ?? props.tweet;
    const createdAt = dayjs(tweet.created_at).format("YYYY-MM-DD HH:mm:ss");
    const userPage = `https://twitter.com/${tweet.user?.screen_name}`;
    const permalink = `https://twitter.com/${tweet.user?.screen_name ?? "_"}/status/${tweet.id_str}`;
    const profileImageUrl = tweet.user?.profile_image_url_https ?? tweet.user?.profile_image_url;
    const styles = useStyles();
    return (
        <Paper>
            <Grid container className={styles.tweetContainer}>
                <Grid item xs="auto">
                    {profileImageUrl && <img className={styles.profile} src={profileImageUrl} />}
                </Grid>
                <Grid item xs={10} className={styles.tweetRightBox}>
                    <Link href={userPage} color="textSecondary" variant="subtitle1">
                        @{tweet.user?.screen_name}&#x2005;/&#x2005;{tweet.user?.name}
                    </Link>
                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: expandTweet(tweet) }} />
                    <Grid container component={"ul"} className={styles.footer}>
                        <Grid item component={"li"}>
                            <Link href={permalink} color="textSecondary" variant="subtitle2">
                                {createdAt}
                            </Link>
                        </Grid>
                        {tweet.source && (
                            <Grid item component="li" className={styles.source}>
                                <Typography
                                    color="textSecondary"
                                    display="inline"
                                    variant="subtitle2"
                                    dangerouslySetInnerHTML={{ __html: tweet.source }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

type ExpandableEntity = {
    indices: [number, number];
    displayText: string;
};

function expandTweet(tweet: Tweet): string {
    let entities: ExpandableEntity[] = [];
    entities.push(
        ...tweet.entities.urls.map((url) => ({
            indices: url.indices,
            displayText: url.expanded_url ? `<a href="${url.expanded_url}">${url.display_url}</a>` : url.display_url,
        }))
    );
    entities.push(
        ...(tweet.entities.media?.map((media) => ({
            indices: media.indices,
            displayText: `<a href="${media.media_url_https}">${media.display_url}</a>`,
        })) ?? [])
    );
    entities.push(
        ...tweet.entities.user_mentions.map((mention) => ({
            indices: mention.indices,
            displayText: `<a href="https://twitter.com/${mention.screen_name}">@${mention.screen_name}</a>`,
        }))
    );
    return expandText(
        tweet.text,
        entities.sort((e1, e2) => e1.indices[0] - e2.indices[0])
    );
}

function expandText(text: string, entities: ExpandableEntity[]): string {
    let expanded = text;
    let extendedLength = 0;
    for (const entity of entities) {
        const fromIndex = entity.indices[0] + extendedLength;
        const toIndex = entity.indices[1] + extendedLength;
        let length = toIndex - fromIndex;
        extendedLength += entity.displayText.length - length;
        expanded = expanded.substring(0, fromIndex) + entity.displayText + expanded.substring(toIndex);
    }
    return expanded;
}
